/* Workspace — Lunch ordering (company-wide, single channel)
 *
 *  載入策略：進頁面拉一次，使用者操作後自動刷新；「今日訂單」tab 每 30s 靜默 polling
 *  /lunch/today 同步別人的動作（「歷史紀錄」tab 不 poll）。
 *
 *  Endpoints:
 *    GET    /api/lunch/today                → { date, menu, orders, me }
 *    PUT    /api/lunch/menu                  { items: [{name, price}] }   any logged-in
 *    POST   /api/lunch/orders                { meal, price, note? }       self only（note ≤ 100 字）
 *    PATCH  /api/lunch/orders/:id            { paid? | note? }            paid: lunch admin；note: owner/admin
 *    DELETE /api/lunch/orders/:id                                          owner/admin
 *    GET    /api/lunch/restaurants           → { restaurants: [{id, name, items, item_count, updated_at}] }  lunch admin
 *    POST   /api/lunch/restaurants            { name, items, overwrite? } lunch admin；同名未帶 overwrite → 409
 *    PUT    /api/lunch/restaurants/:id        { name?, items? }           lunch admin
 *    DELETE /api/lunch/restaurants/:id                                    lunch admin
 */

const fmtHHMM = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

/* Expressive shell only (plan-expressive-inner-pages.md §2(c) page CSS / §4.1 P2). No-op under Bento.
 *  - ≤767 PageHeader action strip (index.html: #F5F2EC, radius 6) → tonal s2, radius 12
 *  - the Bento ≤1479px single-column rule exists because the Bento sidebar + right rail eat the width;
 *    the Expressive rail is 96px, so keep the menu | orders split from 1200px up. */
if (window.GxCore) window.GxCore.injectCss('gx-css-p-lunch', String.raw`
@media (max-width: 767px){
html[data-ui-shell="expressive"] .va2-page-header .hr-lunch-page-action{background:var(--gx-s2)!important;border-radius:12px!important;padding:6px 6px 6px 14px!important}
}
@media (min-width: 1200px){
html[data-ui-shell="expressive"] .gx-main .va2-lunch-grid{grid-template-columns:minmax(0,1.4fr) minmax(0,1fr)!important;gap:20px!important}
}
`);

/* Expressive-only helpers (render-time; Bento gets the very same style object back). */
function lunchAvatarStyle(bento) {
  // initial avatar: Bento sand disc → tertiary container (mockup .av)
  return uiStyle(bento, { background: GxT.color.tc, color: GxT.color.onTc, fontWeight: GxT.weight.title });
}
function lunchGroupLabelStyle(bento) {
  // serif section label (交易部 / 其他 / 合計…) → bold sans, no tracking
  return uiStyle(bento, { fontWeight: GxT.weight.heading, letterSpacing: 0 });
}
function lunchTotalsRowStyle(bento) {
  // Bento's 2px ink "ledger" rule → hairline; tonal footer kept
  return uiStyle(bento, { borderTop: `1px solid ${GxT.color.olv}` });
}

/* OCR 回傳的嵌套格式（items[].options[]）攤平成 {name, price}。
 *
 * 上游格式: {items: [{name, category, options: [{label, price}], tags}]}
 * 輸出格式: [{name, price}]
 *
 * 規則:
 *   - label 是 "default" / 空字串 → 只用 name（單點品項）
 *   - 其他 → name + label 串接（例: 炸排骨 + 飯 = 炸排骨飯）
 *   - 無 options 但有 price → 直接帶 name + price
 *   - 無 options 也無 price → 跳過
 */
function flattenOcrItems(raw) {
  const flat = [];
  for (const item of raw?.items || []) {
    const name = (item.name || '').trim();
    if (!name) continue;
    const options = item.options || [];
    if (options.length > 0) {
      for (const opt of options) {
        const label = (opt.label || '').trim();
        const price = parseInt(opt.price) || 0;
        const fullName = (label === '' || label === 'default') ? name : `${name}${label}`;
        flat.push({ name: fullName, price });
      }
    } else if (item.price != null) {
      flat.push({ name, price: parseInt(item.price) || 0 });
    }
  }
  return flat;
}

/* 圖檔 → resize 至最長邊 maxDim → base64（不含 data: 前綴） */
async function fileToResizedBase64(file, maxDim = 1920, quality = 0.85) {
  const dataUrl = await new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result);
    reader.onerror = () => rej(new Error('讀取檔案失敗'));
    reader.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = () => rej(new Error('圖片格式無法解析'));
    i.src = dataUrl;
  });
  let { width, height } = img;
  const longest = Math.max(width, height);
  if (longest > maxDim) {
    const ratio = maxDim / longest;
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(img, 0, 0, width, height);
  const out = canvas.toDataURL('image/jpeg', quality);
  return out.split(',', 2)[1]; // 去掉 'data:image/jpeg;base64,' 前綴
}

function LunchPage() {
  const toast = useToast();
  const [snap, setSnap] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [lastUpdated, setLastUpdated] = React.useState(null);
  const [tab, setTab] = React.useState('today');
  const [clearOpen, setClearOpen] = React.useState(false);
  const [clearing, setClearing] = React.useState(false);
  // 備註對話框：{ mode: 'order', meal, price } = 備註下單；{ mode: 'edit', orderId, initial } = 改既有訂單備註
  const [noteDialog, setNoteDialog] = React.useState(null);

  const load = React.useCallback(() => {
    return api('GET', '/lunch/today')
      .then(d => { setSnap(d); setLastUpdated(new Date()); })
      .catch(e => toast(e.detail || '載入失敗', 'error'));
  }, [toast]);

  React.useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  /* Silent polling — 多人同時看頁面時，HR 改菜單 / admin 改付款狀態，
   * 其他開著頁面的人也能在 ~30s 內看到最新狀態。
   * 切到「歷史紀錄」tab 時不 poll（節省 API call）。 */
  React.useEffect(() => {
    if (tab !== 'today') return;
    const interval = setInterval(() => {
      // silent — 不顯示 toast、不顯示 loading；只更新 snap
      api('GET', '/lunch/today')
        .then(d => { setSnap(d); setLastUpdated(new Date()); })
        .catch(() => { /* 靜默失敗 */ });
    }, 30000);
    return () => clearInterval(interval);
  }, [tab]);

  if (loading) {
    return (
      <>
        <PageHeader eyebrow="工作空間 · Lunch" title="午餐訂購" italic="Group Lunch" />
        <SkeletonTable rows={5} />
      </>
    );
  }

  const menu   = snap?.menu   || [];
  const orders = snap?.orders || [];
  const me     = snap?.me;
  const today  = snap?.date || todayStr();

  const totalAmount = orders.reduce((s, o) => s + (o.price || 0), 0);
  const paidCount   = orders.filter(o => o.paid).length;
  // 付款狀態切換與菜單編輯僅 admin/hr/admin_assistant（行政助理）可操作。
  const LUNCH_ADMIN_ROLES = ['admin', 'hr', 'admin_assistant'];
  const isAdmin = LUNCH_ADMIN_ROLES.includes(window._USER?.role)
               || (window._USER?.roles || []).some(r => LUNCH_ADMIN_ROLES.includes(r));

  // 方案 A：點菜單卡片 → 直接下單一份（取代原本下拉選單 + 下單兩步）。
  // note（選填）：卡片上「備註」affordance 開對話框填寫後帶入（如「不要辣」）。
  const orderMeal = async (m, note) => {
    try {
      await api('POST', '/lunch/orders', { meal: m.name, price: m.price ?? 0, ...(note ? { note } : {}) });
      toast('已下單', 'ok');
      load();
    } catch (e) { toast(e.detail || '下單失敗', 'error'); }
  };

  // 改既有訂單備註（本人/admin；傳空字串 = 清除備註）
  const saveOrderNote = async (orderId, note) => {
    try {
      await api('PATCH', `/lunch/orders/${orderId}`, { note });
      toast(note ? '已更新備註' : '已清除備註', 'ok');
      load();
    } catch (e) { toast(e.detail || '備註更新失敗', 'error'); }
  };

  // 切換單筆付款狀態（admin only — 後端 paid 欄位僅 lunch admin 可改）
  const togglePaid = async (o) => {
    try {
      await api('PATCH', `/lunch/orders/${o.id}`, { paid: !o.paid });
      load();
    } catch (e) { toast(e.detail || '失敗', 'error'); }
  };

  // 刪除訂單（本人 / admin — 與後端 DELETE 權限一致）
  const deleteOrder = async (o) => {
    if (!confirm('刪除此訂單？')) return;
    try {
      await api('DELETE', `/lunch/orders/${o.id}`);
      load();
    } catch (e) { toast(e.detail || '失敗', 'error'); }
  };

  // 整人一鍵已付（admin only）— 把該人所有未付訂單一次標記已付
  const markAllPaid = async (personOrders) => {
    const unpaid = personOrders.filter(o => !o.paid);
    if (unpaid.length === 0) return;
    try {
      await Promise.all(unpaid.map(o => api('PATCH', `/lunch/orders/${o.id}`, { paid: true })));
      toast(`已標記 ${unpaid.length} 筆為已付`, 'ok');
    } catch (e) {
      toast(e.detail || '部分訂單更新失敗', 'error');
    }
    load();
  };

  return (
    <>
      <PageHeader
        eyebrow="工作空間 · Lunch"
        title="午餐訂購"
        italic="Group Lunch"
        action={
          tab === 'today' && (
            <div className="hr-lunch-page-action" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
                {today}{lastUpdated && ` · 更新於 ${fmtHHMM(lastUpdated)}`}
              </span>
              <Button size="sm" variant="ghost" icon="refresh" onClick={load}>重整</Button>
            </div>
          )
        }
      />

      <Tabs
        tabs={[
          { key: 'today',   label: '今日訂單', icon: 'coffee' },
          { key: 'history', label: '歷史紀錄', icon: 'calendar' },
        ]}
        value={tab}
        onChange={setTab}
      />

      {tab === 'history' && <LunchHistoryTab isAdmin={isAdmin} />}

      {tab === 'today' && (() => {
        // 餐點熱度：每道被訂幾份（菜單卡片顯示「×N」+ 右側叫餐清單用）
        const mealCounts = orders.reduce((acc, o) => {
          acc[o.meal] = (acc[o.meal] || 0) + 1;
          return acc;
        }, {});
        // 我自己點了哪幾道、各幾份（菜單卡片高亮「你點了」用）
        const myMealCounts = orders.reduce((acc, o) => {
          if (o.uid === me?.uid) acc[o.meal] = (acc[o.meal] || 0) + 1;
          return acc;
        }, {});
        // 依餐點彙總（份數 + 小計 + 備註清單），給左欄「餐點數量總結」叫餐清單用，依份數多→少排序。
        // notes：該餐點各份的備註（打電話訂餐的人要唸給店家聽，如「其中 1 份不要辣」）。
        const summarize = (list) => Object.values(
          list.reduce((acc, o) => {
            if (!acc[o.meal]) acc[o.meal] = { meal: o.meal, qty: 0, subtotal: 0, notes: [] };
            acc[o.meal].qty += 1;
            acc[o.meal].subtotal += (o.price || 0);
            if (o.note) acc[o.meal].notes.push(o.note);
            return acc;
          }, {})
        ).sort((a, b) => b.qty - a.qty || a.meal.localeCompare(b.meal));
        // 交易部（部門 code = TRD）vs 其他，各自的叫餐清單
        const trdSummary   = summarize(orders.filter(o => o.dept_code === 'TRD'));
        const otherSummary = summarize(orders.filter(o => o.dept_code !== 'TRD'));
        const unpaidCount = orders.length - paidCount;
        const allPaid = orders.length > 0 && unpaidCount === 0;
        // 結算對帳分組：交易部（員工部門 code = TRD）vs 其他（含未設部門 / 非員工帳號）
        const statOf = (list) => ({
          qty: list.length,
          total: list.reduce((s, o) => s + (o.price || 0), 0),
          unpaidQty: list.filter(o => !o.paid).length,
          unpaidTotal: list.filter(o => !o.paid).reduce((s, o) => s + (o.price || 0), 0),
        });
        const trdStat   = statOf(orders.filter(o => o.dept_code === 'TRD'));
        const otherStat = statOf(orders.filter(o => o.dept_code !== 'TRD'));
        const grandStat = statOf(orders);
        // 今日訂單也依交易部 / 其他分組顯示
        const orderGroups = [
          { label: '交易部', rows: orders.filter(o => o.dept_code === 'TRD') },
          { label: '其他',   rows: orders.filter(o => o.dept_code !== 'TRD') },
        ];
        // 同一人的訂單聚成人員區塊（部門分組內），依該人最早一筆 created_at 排序（先下單的人在前）
        const personsOf = (rows) => {
          const map = new Map();
          for (const o of rows) {
            const key = o.uid ?? `name:${o.name}`;
            if (!map.has(key)) map.set(key, { uid: o.uid, name: o.name, orders: [], first: o.created_at || '' });
            const p = map.get(key);
            p.orders.push(o);
            if ((o.created_at || '') < p.first) p.first = o.created_at || '';
          }
          return [...map.values()].sort((a, b) => a.first.localeCompare(b.first));
        };
        // 人員區塊渲染（部門分組共用）— pi 用來去掉每組首個區塊的上框線，避免跟組標頭雙線
        const renderPersonBlock = (p, pi) => {
          const isOwn = p.uid === me?.uid;
          const subtotal = p.orders.reduce((s, o) => s + (o.price || 0), 0);
          const unpaidOrders = p.orders.filter(o => !o.paid);
          return (
            <div key={p.uid ?? p.name} style={{
              padding: '8px 18px 9px',
              borderTop: pi === 0 ? 'none' : `1px solid ${HR.ruleSoft}`,
            }}>
              {/* 區塊頭：頭像 + 姓名一次；多筆時顯示小計（admin 另有整人一鍵已付）*/}
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={lunchAvatarStyle({
                  width: 26, height: 26, borderRadius: '50%',
                  background: '#D6CEB9', color: HR.ink,
                  display: 'grid', placeItems: 'center', flexShrink: 0,
                  fontSize: FontSizes.trading_md, fontWeight: 600, fontFamily: HR.serif,
                })}>{(p.name || '?').slice(0, 1)}</span>
                <span style={{
                  fontWeight: uiPick(500, GxT.weight.heading), color: HR.ink, fontSize: FontSizes.sm,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
                }}>{p.name}</span>
                <span style={{ flex: 1 }} />
                {isAdmin && p.orders.length > 1 && unpaidOrders.length > 0 && (
                  <button
                    onClick={() => markAllPaid(p.orders)}
                    title={`將 ${p.name} 的 ${unpaidOrders.length} 筆未付訂單全部標記為已付`}
                    style={uiStyle({
                      background: 'transparent', border: `1px solid ${HR.rule}`,
                      borderRadius: 3, padding: '2px 8px', flexShrink: 0,
                      fontSize: FontSizes.trading_md, color: HR.inkSoft, cursor: 'pointer',
                      transition: 'color 120ms, background 120ms, border-color 120ms',
                    }, { borderRadius: GxT.radius.chip, padding: '3px 10px', fontWeight: GxT.weight.label })}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = HR.accent;
                      e.currentTarget.style.background = HR.accentSoft;
                      e.currentTarget.style.borderColor = HR.accent;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = HR.inkSoft;
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = HR.rule;
                    }}
                  >全部已付</button>
                )}
                {p.orders.length > 1 && (
                  <span style={{
                    fontFamily: HR.mono, fontSize: FontSizes.trading_lg, fontWeight: 500,
                    fontVariantNumeric: 'tabular-nums', color: HR.inkSoft, flexShrink: 0,
                  }}>小計 {fmtMoney(subtotal)}</span>
                )}
              </div>
              {/* 子列：餐點 + 備註 + 價格 + 付款 Pill + 操作 icon（單行緊湊）*/}
              {p.orders.map((o) => {
                const canOperate = isOwn || isAdmin;  // 備註/刪除 — 與後端 note/DELETE 權限一致
                return (
                  <div key={o.id}
                    className="hr-lunch-order-row"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '3px 0 3px 35px', borderRadius: uiPick(3, GxT.radius.chip),
                      transition: 'background 120ms',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = HR.surfaceAlt}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <span style={{ flex: 1, minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        fontFamily: HR.serif, fontSize: FontSizes.sm, color: HR.inkSoft,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{o.meal}</span>
                      {/* 備註 — 本人 / lunch admin 可點文字改；其他人唯讀 */}
                      {o.note && (
                        <span
                          title={canOperate ? `備註：${o.note}（點擊修改）` : `備註：${o.note}`}
                          onClick={canOperate ? () => setNoteDialog({ mode: 'edit', orderId: o.id, initial: o.note }) : undefined}
                          style={{
                            fontSize: FontSizes.trading_lg, color: HR.inkFaint,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            cursor: canOperate ? 'pointer' : 'default',
                            display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0,
                          }}>
                          <Icon name="edit" size={10} stroke={2} />{o.note}
                        </span>
                      )}
                    </span>
                    <span style={{
                      fontFamily: HR.mono, fontWeight: 500, fontSize: FontSizes.sm,
                      fontVariantNumeric: 'tabular-nums', color: HR.ink, flexShrink: 0,
                    }}>{fmtMoney(o.price)}</span>
                    {/* 付款 Pill — admin 點擊即切換（後端 paid 僅 lunch admin 可改）；其他人純顯示 */}
                    {isAdmin ? (
                      <span
                        title="點擊切換付款狀態"
                        onClick={() => togglePaid(o)}
                        style={{ cursor: 'pointer', display: 'inline-flex', flexShrink: 0 }}>
                        {o.paid ? <Pill tone="accent" dot>已付</Pill> : <Pill tone="warn" dot>未付</Pill>}
                      </span>
                    ) : (
                      o.paid ? <Pill tone="accent" dot>已付</Pill> : <Pill tone="warn" dot>未付</Pill>
                    )}
                    {/* 操作 icon（常駐、水平）：備註 ✎ / 刪除 🗑 — 皆為本人 or lunch admin */}
                    {canOperate ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                        <button
                          title={o.note ? '修改備註' : '加備註'}
                          onClick={() => setNoteDialog({ mode: 'edit', orderId: o.id, initial: o.note || '' })}
                          style={{
                            background: 'transparent', border: 'none', cursor: 'pointer',
                            color: HR.inkFaint, padding: 3, borderRadius: uiPick(3, GxT.radius.chip),
                            display: 'inline-flex', lineHeight: 0, transition: 'color 120ms',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.color = HR.accent}
                          onMouseLeave={(e) => e.currentTarget.style.color = HR.inkFaint}
                        ><Icon name="edit" size={13} stroke={2} /></button>
                        <button
                          title="刪除訂單"
                          onClick={() => deleteOrder(o)}
                          style={{
                            background: 'transparent', border: 'none', cursor: 'pointer',
                            color: HR.inkFaint, padding: 3, borderRadius: uiPick(3, GxT.radius.chip),
                            display: 'inline-flex', lineHeight: 0, transition: 'color 120ms',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.color = HR.down}
                          onMouseLeave={(e) => e.currentTarget.style.color = HR.inkFaint}
                        ><Icon name="trash" size={13} stroke={2} /></button>
                      </span>
                    ) : (
                      /* 無操作權者補等寬 spacer，讓價格/Pill 欄位上下對齊 */
                      <span style={{ width: 40, flexShrink: 0 }} />
                    )}
                  </div>
                );
              })}
            </div>
          );
        };
        return (
      <div className="va2-lunch-grid" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 18, alignItems: 'start' }}>
        {/* Left: 菜單 hero（點卡片即下單）+ 餐點數量總結 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card
            className="hr-lunch-menu-card"
            eyebrow="Menu"
            title="今日菜單"
            italic={menu.length > 0 ? `${menu.length} 項 · 點卡片下單` : `${menu.length} 項`}
            padding={menu.length > 0 ? '16px' : '22px 24px'}
            right={isAdmin ? (
              <span style={{ display: 'inline-flex', gap: 6 }}>
                <MenuCreator currentCount={menu.length} orderCount={orders.length} onSaved={load} />
                <MenuEditor initial={menu} onSaved={load} />
                <RestaurantManager currentMenu={menu} />
              </span>
            ) : null}
          >
            {menu.length === 0 ? (
              <div style={{ padding: 10, fontSize: FontSizes.xxs, color: HR.inkFaint, fontStyle: uiPick('italic', 'normal') }}>
                尚未建立菜單。{isAdmin ? '點上方「建立」用菜單照片 OCR 快速建立' : '請等 HR 建立今日菜單'}
              </div>
            ) : (
              <div className="hr-lunch-dish-grid" style={{
                // 最小卡寬 172px — footer（點餐/再點一份 + 備註 + ×N）最窄時才塞得進單行不折行
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(172px, 1fr))', gap: 12,
              }}>
                {menu.map((m, i) => (
                  <DishCard
                    key={i}
                    item={m}
                    count={mealCounts[m.name] || 0}
                    mineCount={myMealCounts[m.name] || 0}
                    onOrder={() => orderMeal(m)}
                    onOrderWithNote={() => setNoteDialog({ mode: 'order', meal: m, initial: '' })}
                  />
                ))}
              </div>
            )}
          </Card>

          {/* 餐點數量總結 — 依交易部 / 其他分開的叫餐清單（有訂單才顯示，給訂便當的人對數量）*/}
          {orders.length > 0 && <MealSummaryCard trd={trdSummary} other={otherSummary} />}
        </div>

        {/* Right: 今日訂單（部門分組內以人為區塊，同一人訂單排在一起）+ 結算對帳（交易部 / 其他）*/}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card
            className="hr-lunch-order-card"
            padding="0"
            eyebrow="Today"
            title="今日訂單"
            italic={`${orders.length} 筆`}
          >
            {/* Stats strip — 筆數 / 已付（總額移到下方「結算對帳」卡）*/}
            {orders.length > 0 && (
              <div className="hr-lunch-stats-strip" style={{
                display: 'flex', alignItems: 'center',
                gap: 14, fontSize: FontSizes.trading_xl,
                padding: '12px 18px', borderBottom: `1px solid ${HR.ruleSoft}`,
                background: HR.surfaceAlt,
              }}>
                {/* 左：admin-only 清空按鈕 */}
                {isAdmin && (
                  <button
                    onClick={() => setClearOpen(true)}
                    title={`清空今日 ${orders.length} 筆訂單`}
                    style={uiStyle({
                      background: 'transparent', border: `1px solid ${HR.rule}`,
                      borderRadius: 3, padding: '4px 10px',
                      fontSize: FontSizes.trading_lg, color: HR.inkSoft, cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      transition: 'color 120ms, background 120ms, border-color 120ms',
                    }, { borderRadius: GxT.radius.chip, padding: '5px 12px', fontWeight: GxT.weight.label })}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = HR.down;
                      e.currentTarget.style.background = uiPick('#FCE4E4', GxCore.tone('down').bg);
                      e.currentTarget.style.borderColor = HR.down;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = HR.inkSoft;
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = HR.rule;
                    }}
                  >
                    清空全部
                  </button>
                )}
                <span style={{ flex: 1 }} />
                <span style={{ color: HR.inkFaint, fontFamily: HR.mono }}>
                  {orders.length} 筆
                </span>
                <span style={{
                  fontFamily: HR.mono, fontWeight: 500,
                  color: allPaid ? HR.accent : HR.warn,
                }}>
                  {paidCount}/{orders.length} 已付
                </span>
              </div>
            )}
            {orders.length === 0 ? (
              <EmptyState icon="coffee" title="本日尚無訂單" hint="點左側菜單卡片即可下單" />
            ) : (
              <div className="hr-lunch-order-list">
                {orderGroups.map((g, gi) => {
                  const gPaid = g.rows.filter(o => o.paid).length;
                  const gAllPaid = g.rows.length > 0 && gPaid === g.rows.length;
                  return (
                    <div key={g.label}>
                      {/* 部門小標 — 該部門筆數 + 已付 */}
                      <div style={{
                        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                        padding: '9px 18px', background: HR.surfaceAlt,
                        borderTop: gi === 0 ? 'none' : `1px solid ${HR.rule}`,
                        borderBottom: `1px solid ${HR.ruleSoft}`,
                      }}>
                        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
                          <span style={lunchGroupLabelStyle({ fontFamily: HR.serif, fontSize: FontSizes.sm, color: HR.ink })}>{g.label}</span>
                          <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>{g.rows.length} 筆</span>
                        </span>
                        {g.rows.length > 0 && (
                          <span style={{
                            fontFamily: HR.mono, fontSize: FontSizes.trading_lg, fontWeight: 500,
                            color: gAllPaid ? HR.accent : HR.warn,
                          }}>{gPaid}/{g.rows.length} 已付</span>
                        )}
                      </div>
                      {g.rows.length === 0 ? (
                        <div style={{ padding: '10px 18px', fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontStyle: uiPick('italic', 'normal') }}>
                          （無訂單）
                        </div>
                      ) : personsOf(g.rows).map((p, pi) => renderPersonBlock(p, pi))}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* 結算對帳 — 依部門分「交易部 / 其他」結算（應使用者需求）*/}
          {orders.length > 0 && (
            <SettlementCard trd={trdStat} other={otherStat} grand={grandStat} />
          )}
        </div>
      </div>
        );
      })()}

      {/* 備註對話框 — 備註下單（order）/ 修改既有訂單備註（edit）共用 */}
      {noteDialog && (
        <NoteDialog
          mode={noteDialog.mode}
          meal={noteDialog.meal}
          initial={noteDialog.initial || ''}
          onCancel={() => setNoteDialog(null)}
          onSubmit={async (note) => {
            if (noteDialog.mode === 'order') await orderMeal(noteDialog.meal, note);
            else await saveOrderNote(noteDialog.orderId, note);
            setNoteDialog(null);
          }}
        />
      )}

      {/* Admin: 清空今日全部訂單 — confirm */}
      <ConfirmDialog
        open={clearOpen}
        title="清空今日訂單"
        message={
          `將永久刪除今日全部 ${orders.length} 筆訂單（含已付與未付），\n` +
          `菜單不會被動到。此操作無法復原。\n\n` +
          `確認清空？`
        }
        danger
        confirmLabel={`清空 ${orders.length} 筆訂單`}
        busy={clearing}
        onCancel={() => setClearOpen(false)}
        onConfirm={async () => {
          setClearing(true);
          try {
            const resp = await api('POST', '/lunch/orders/clear-today');
            toast(`已清空 ${resp?.cleared ?? 0} 筆訂單`, 'ok');
            setClearOpen(false);
            await load();
          } catch (e) {
            toast(e.detail || '清空失敗', 'error');
          } finally {
            setClearing(false);
          }
        }}
      />
    </>
  );
}

const WEEKDAY_TW = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'];

function fmtHistoryDate(dateStr) {
  // 'YYYY-MM-DD' → '5/11 · 週一' (omit year unless different from current year)
  const d = new Date(dateStr + 'T00:00:00');
  const yr = d.getFullYear();
  const currentYr = new Date().getFullYear();
  const prefix = yr === currentYr ? '' : `${yr}/`;
  return `${prefix}${d.getMonth() + 1}/${d.getDate()} · ${WEEKDAY_TW[d.getDay()]}`;
}

function fmtRelativeDay(dateStr) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + 'T00:00:00');
  const diff = Math.round((today - d) / 86400000);
  if (diff === 0) return '今天';
  if (diff === 1) return '昨天';
  if (diff === 2) return '前天';
  return null;
}

/* ─────────────────────────  History tab (lazy accordion)  ────
 * 初載入只抓「日期摘要」(/lunch/history/summary)，每天一行：筆數 + 總額 + 已付數。
 * 點某一天 → 才打 /lunch/orders/history?start_date=X&end_date=X 抓那天的明細，
 * 結果 cache 在 dayOrdersCache 不會重複抓。
 *
 * 預設展開最近一天（如果有資料）— 體貼預設值，但不會一次撈全部。
 *
 * Read-only — no paid-toggle / delete here (use today tab for current-day ops). */
function LunchHistoryTab({ isAdmin }) {
  const toast = useToast();
  const [days, setDays]           = React.useState(30);      // 回溯天數
  const [userId, setUserId]       = React.useState('');      // '' = all (admin only)
  const [employees, setEmployees] = React.useState([]);
  const [summary, setSummary]     = React.useState(null);    // null = loading
  const [loading, setLoading]     = React.useState(true);

  // 展開狀態 + 每日明細 cache
  const [expanded, setExpanded]         = React.useState(() => new Set());
  const [dayOrdersCache, setDayOrders]  = React.useState(() => new Map());
  const [dayLoading, setDayLoading]     = React.useState(() => new Set());

  // Admin: load employee list once for filter
  React.useEffect(() => {
    if (!isAdmin) return;
    api('GET', '/employees?page_size=200')
      .then(resp => setEmployees(resp?.items || []))
      .catch(() => setEmployees([]));
  }, [isAdmin]);

  // 抓 summary
  const loadSummary = React.useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams({ days: String(days) });
    if (isAdmin && userId) qs.set('user_id', userId);
    try {
      const resp = await api('GET', `/lunch/history/summary?${qs.toString()}`);
      setSummary(resp);
      // 預設展開最近一天（且還沒展開過時才動）
      const recent = resp?.days?.[0]?.date;
      if (recent) {
        setExpanded(prev => prev.size === 0 ? new Set([recent]) : prev);
      }
    } catch (e) {
      toast(e.detail || '載入歷史失敗', 'error');
      setSummary({ days: [] });
    } finally {
      setLoading(false);
    }
  }, [days, userId, isAdmin]);

  React.useEffect(() => { loadSummary(); }, [loadSummary]);

  // filter / days 變動時清掉 cache（避免員工切換後看到別人快取）
  React.useEffect(() => {
    setExpanded(new Set());
    setDayOrders(new Map());
    setDayLoading(new Set());
  }, [userId]);

  // 載入某一天的明細（lazy + cache）
  const loadDay = React.useCallback(async (date) => {
    if (dayOrdersCache.has(date)) return;  // 已有快取
    setDayLoading(prev => new Set(prev).add(date));
    try {
      const qs = new URLSearchParams({ start_date: date, end_date: date });
      if (isAdmin && userId) qs.set('user_id', userId);
      const resp = await api('GET', `/lunch/orders/history?${qs.toString()}`);
      setDayOrders(prev => new Map(prev).set(date, resp?.orders || []));
    } catch (e) {
      toast(e.detail || '載入該日明細失敗', 'error');
      setDayOrders(prev => new Map(prev).set(date, []));
    } finally {
      setDayLoading(prev => { const s = new Set(prev); s.delete(date); return s; });
    }
  }, [dayOrdersCache, isAdmin, userId, toast]);

  const toggle = (date) => {
    const willOpen = !expanded.has(date);
    setExpanded(prev => {
      const s = new Set(prev);
      if (s.has(date)) s.delete(date); else s.add(date);
      return s;
    });
    if (willOpen) loadDay(date);
  };

  // 預設展開最近一天 → 也要 trigger 載入
  React.useEffect(() => {
    expanded.forEach(d => { if (!dayOrdersCache.has(d) && !dayLoading.has(d)) loadDay(d); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const allDays = summary?.days || [];
  const totalCount      = allDays.reduce((s, d) => s + d.order_count, 0);
  const totalAmount     = allDays.reduce((s, d) => s + d.total, 0);
  const totalPaid       = allDays.reduce((s, d) => s + d.paid_count, 0);
  const totalUnpaid     = totalCount - totalPaid;
  const totalUnpaidAmt  = allDays.reduce((s, d) => s + (d.unpaid_total || 0), 0);

  return (
    <Card padding="0" eyebrow="History" title="歷史訂單"
      italic={loading
        ? '載入中…'
        : `${allDays.length} 天 · ${totalCount} 筆 · ${fmtMoney(totalAmount)}`
      }>
      {/* Toolbar */}
      <div className="hr-lunch-history-toolbar" style={{
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        padding: '14px 24px', borderBottom: `1px solid ${HR.ruleSoft}`, background: HR.surfaceAlt,
      }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', background: '#fff', border: `1px solid ${HR.rule}`, borderRadius: uiPick(4, GxT.radius.input),
        }}>
          <Icon name="calendar" size={12} stroke={2} />
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: FontSizes.trading_md, padding: '4px 4px' }}>
            <option value={7}>近 7 天</option>
            <option value={30}>近 30 天</option>
            <option value={90}>近 90 天</option>
            <option value={180}>近半年</option>
            <option value={365}>近一年</option>
          </select>
        </div>
        {isAdmin && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', background: '#fff', border: `1px solid ${HR.rule}`, borderRadius: uiPick(4, GxT.radius.input),
          }}>
            <Icon name="file-text" size={12} stroke={2} />
            <select value={userId} onChange={(e) => setUserId(e.target.value)}
              style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: FontSizes.trading_md, padding: '4px 0', width: 160 }}>
              <option value="">全部員工</option>
              {employees.filter(e => e.user_id).map(e => (
                <option key={e.id} value={e.user_id}>{e.name}</option>
              ))}
            </select>
          </div>
        )}
        {!isAdmin && (
          <span style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontStyle: uiPick('italic', 'normal') }}>
            僅顯示你自己的訂單
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
          點日期展開明細
        </span>
      </div>

      {loading ? (
        <SkeletonTable rows={5} />
      ) : allDays.length === 0 ? (
        <EmptyState icon="coffee" title="此區間沒有訂單紀錄" hint="調整日期或員工篩選試試" />
      ) : (
        <div>
          {allDays.map((d, gi) => (
            <HistoryDayRow
              key={d.date}
              day={d}
              gi={gi}
              isOpen={expanded.has(d.date)}
              isLoading={dayLoading.has(d.date)}
              orders={dayOrdersCache.get(d.date)}
              onToggle={() => toggle(d.date)}
            />
          ))}
          {/* Range reconciliation footer — 區間對帳；天數/筆數/總額已在卡片標題列，這裡只看付款狀態 */}
          {totalCount > 0 && (
            <div className="hr-lunch-history-footer" style={lunchTotalsRowStyle({
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 24px', borderTop: `2px solid ${HR.ink}`,
              background: HR.surfaceAlt, fontSize: FontSizes.xxs,
            })}>
              <span style={lunchGroupLabelStyle({ fontFamily: HR.serif, fontSize: FontSizes.xs, color: HR.inkSoft, letterSpacing: '0.04em' })}>
                區間對帳
              </span>
              {totalUnpaid > 0 ? (
                <span style={{
                  fontFamily: HR.mono, fontSize: FontSizes.md, fontWeight: 600, color: HR.warn,
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  未付 {totalUnpaid} 筆 / {fmtMoney(totalUnpaidAmt)}
                </span>
              ) : (
                <span style={{ fontFamily: HR.mono, fontSize: FontSizes.md, fontWeight: 500, color: HR.accent }}>
                  全部已付 · {totalCount} 筆
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/* Single accordion row — header (always visible) + lazy-loaded order list */
function HistoryDayRow({ day, gi, isOpen, isLoading, orders, onToggle }) {
  const dayUnpaid = day.order_count - day.paid_count;
  const allPaid = dayUnpaid === 0 && day.order_count > 0;
  const rel = fmtRelativeDay(day.date);

  return (
    <div style={{
      background: gi % 2 === 1 ? GxCore.alpha(HR.surfaceAlt, '40') : 'transparent',
      borderTop: gi === 0 ? 'none' : `1px solid ${HR.rule}`,
    }}>
      {/* Day header — clickable */}
      <div
        className="hr-lunch-day-header"
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 24px',
          cursor: 'pointer',
          transition: 'background 120ms',
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = HR.surface}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      >
        {/* Chevron */}
        <span style={{
          display: 'inline-block', width: 14,
          color: HR.inkFaint, fontSize: FontSizes.trading_sm,
          transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 120ms',
        }}>▶</span>

        {/* Date */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flex: 1 }}>
          <span style={{ fontFamily: HR.serif, fontSize: FontSizes.md, fontWeight: uiPick(500, GxT.weight.heading), color: HR.ink, letterSpacing: '-0.01em' }}>
            {fmtHistoryDate(day.date)}
          </span>
          {rel && (
            <span className="hr-lunch-rel-badge" style={uiStyle({
              fontSize: FontSizes.trading_sm, padding: '2px 7px', borderRadius: 10,
              background: HR.accentSoft, color: HR.accent,
              fontFamily: HR.mono, letterSpacing: '0.04em',
            }, { borderRadius: GxT.radius.chipSm, fontFamily: GxT.font, fontWeight: GxT.weight.label, letterSpacing: 0 })}>{rel}</span>
          )}
          <span className="hr-lunch-day-raw-date" style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontFamily: HR.mono }}>
            {day.date}
          </span>
        </div>

        {/* Day stats */}
        <div className="hr-lunch-day-stats" style={{ display: 'flex', alignItems: 'baseline', gap: 14, fontSize: FontSizes.trading_xl }}>
          <span style={{ color: HR.inkFaint, fontFamily: HR.mono }}>
            {day.order_count} 筆
          </span>
          <span style={{
            fontFamily: HR.mono,
            color: allPaid ? HR.accent : HR.warn,
            fontWeight: 500,
          }}>
            {day.paid_count}/{day.order_count} 已付
          </span>
          <span style={{
            fontFamily: HR.mono, fontSize: FontSizes.xs, fontWeight: 600, color: HR.ink,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {fmtMoney(day.total)}
          </span>
        </div>
      </div>

      {/* Expanded body */}
      {isOpen && (
        <div style={{ borderTop: `1px solid ${HR.ruleSoft}`, background: HR.surface }}>
          {isLoading ? (
            <div style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 10, color: HR.inkFaint, fontSize: FontSizes.xxs }}>
              <Spinner size={14} /> 載入該日明細…
            </div>
          ) : !orders || orders.length === 0 ? (
            <div style={{ padding: '20px 24px', fontSize: FontSizes.xxs, color: HR.inkFaint, fontStyle: uiPick('italic', 'normal') }}>
              （此日無訂單）
            </div>
          ) : (
            <div className="hr-lunch-hist-table">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id}
                    style={{ borderTop: `1px solid ${HR.ruleSoft}`, transition: 'background 120ms' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = HR.surfaceAlt}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '11px 16px 11px 50px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <span style={lunchAvatarStyle({
                          width: 24, height: 24, borderRadius: '50%',
                          background: '#D6CEB9', color: HR.ink,
                          display: 'grid', placeItems: 'center',
                          fontSize: FontSizes.trading_md, fontWeight: 600, fontFamily: HR.serif,
                        })}>{(o.name || '?').slice(0, 1)}</span>
                        <span style={{ fontWeight: uiPick(500, GxT.weight.heading), color: HR.ink }}>{o.name}</span>
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px', fontFamily: HR.serif, fontSize: FontSizes.trading_md, color: HR.ink }}>
                      {o.meal}
                      {o.note && (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 8,
                          fontFamily: HR.sans, fontSize: FontSizes.trading_lg, color: HR.inkFaint,
                        }} title={`備註：${o.note}`}>
                          <Icon name="edit" size={10} stroke={2} />{o.note}
                        </span>
                      )}
                    </td>
                    <td style={{
                      padding: '11px 16px', textAlign: 'right',
                      fontFamily: HR.mono, fontWeight: 500,
                      fontVariantNumeric: 'tabular-nums', color: HR.ink,
                    }}>
                      {fmtMoney(o.price)}
                    </td>
                    <td style={{ padding: '11px 24px 11px 16px', textAlign: 'right', width: 80, whiteSpace: 'nowrap' }}>
                      {o.paid
                        ? <Pill tone="accent" dot>已付</Pill>
                        : <Pill tone="warn" dot>未付</Pill>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* 餐點數量總結 — 依「交易部 / 其他」分區，各自彙總成叫餐清單：每道顯示份數 + 小計。
 * 純展示，資料由 LunchPage 用 summarize() 依部門算好後傳入（trd / other），各區依份數多→少排序。 */
function MealSummaryCard({ trd, other }) {
  const groups = [
    { label: '交易部', rows: trd },
    { label: '其他',   rows: other },
  ];
  const allRows = [...trd, ...other];
  const totalQty = allRows.reduce((s, m) => s + m.qty, 0);
  const totalAmt = allRows.reduce((s, m) => s + m.subtotal, 0);
  const totalItems = new Set(allRows.map(m => m.meal)).size;
  return (
    <Card
      className="hr-lunch-summary-card"
      eyebrow="Summary"
      title="餐點數量總結"
      italic={`${totalItems} 品項 · ${totalQty} 份`}
      padding="0"
    >
      {groups.map((g, gi) => {
        const gQty = g.rows.reduce((s, m) => s + m.qty, 0);
        const gAmt = g.rows.reduce((s, m) => s + m.subtotal, 0);
        return (
          <div key={g.label}>
            {/* 部門小標 — 該部門份數 + 小計 */}
            <div style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              padding: '10px 14px', background: HR.surfaceAlt,
              borderTop: gi === 0 ? 'none' : `1px solid ${HR.rule}`,
              borderBottom: `1px solid ${HR.ruleSoft}`,
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
                <span style={lunchGroupLabelStyle({ fontFamily: HR.serif, fontSize: FontSizes.sm, color: HR.ink })}>{g.label}</span>
                <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>{gQty} 份</span>
              </span>
              <span style={{
                fontFamily: HR.mono, fontSize: FontSizes.xs, fontWeight: 600, color: HR.inkSoft,
                fontVariantNumeric: 'tabular-nums',
              }}>{fmtMoney(gAmt)}</span>
            </div>
            {/* 該部門的叫餐清單 */}
            {g.rows.length === 0 ? (
              <div style={{ padding: '10px 14px', fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontStyle: uiPick('italic', 'normal') }}>
                （無訂單）
              </div>
            ) : g.rows.map((m, i) => (
              <div key={i}
                style={{
                  padding: '9px 14px', fontSize: FontSizes.xs,
                  borderBottom: i < g.rows.length - 1 ? `1px solid ${HR.ruleSoft}` : 'none',
                  transition: 'background 120ms',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = HR.surfaceAlt}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                    <span style={{ fontFamily: HR.serif, color: HR.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.meal}
                    </span>
                    <span style={uiStyle({
                      fontSize: FontSizes.trading_sm, padding: '1px 6px', borderRadius: 8,
                      background: HR.accentSoft, color: HR.accent,
                      fontFamily: HR.mono, letterSpacing: '0.02em', flexShrink: 0,
                    }, { padding: '1px 7px', borderRadius: GxT.radius.chipSm, fontWeight: GxT.weight.label, letterSpacing: 0 })}>×{m.qty}</span>
                  </span>
                  <span style={{
                    fontFamily: HR.mono, color: HR.inkSoft,
                    fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                  }}>
                    {fmtMoney(m.subtotal)}
                  </span>
                </div>
                {/* 備註逐條列出 — 叫餐的人要唸給店家聽（如「其中 1 份不要辣」） */}
                {(m.notes || []).length > 0 && (
                  <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {m.notes.map((n, ni) => (
                      <span key={ni} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        fontSize: FontSizes.trading_lg, color: HR.inkFaint, paddingLeft: 2,
                      }}>
                        <Icon name="edit" size={10} stroke={2} />{n}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}
      {/* 總計 footer — 跟訂單表同款黑線 + tabular nums */}
      <div style={lunchTotalsRowStyle({
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 14px', borderTop: `2px solid ${HR.ink}`, background: HR.surfaceAlt,
        fontSize: FontSizes.xxs,
      })}>
        <span style={lunchGroupLabelStyle({ fontFamily: HR.serif, fontSize: FontSizes.xs, color: HR.inkSoft, letterSpacing: '0.04em' })}>
          合計 {totalQty} 份
        </span>
        <span style={{
          fontFamily: HR.mono, fontSize: FontSizes.md, fontWeight: 600, color: HR.ink,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {fmtMoney(totalAmt)}
        </span>
      </div>
    </Card>
  );
}

/* 菜單卡片 — 方案 A 核心：點一下直接下單一份（取代原本「下拉選單 + 下單」兩步）。
 *   count           : 全體訂購份數（熱度，footer 顯示 ×N；已點過時隱藏——與「你點了 ×N」浮標重複）
 *   mineCount       : 我自己點了幾份（>0 → 高亮 + 標記「你點了」；footer 備註縮成純 icon 保持單行）
 *   onOrderWithNote : 卡片 footer「備註」affordance — 開對話框填備註後下單
 *                     （span + stopPropagation，避免 button 巢狀與誤觸直接下單）
 */
function DishCard({ item, count, mineCount, onOrder, onOrderWithNote }) {
  const [hover, setHover] = React.useState(false);
  const mine = mineCount > 0;
  return (
    <button
      onClick={onOrder}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="hr-lunch-dish"
      style={uiStyle({
        position: 'relative', textAlign: 'left', cursor: 'pointer', width: '100%',
        fontFamily: HR.sans, display: 'flex', flexDirection: 'column', gap: 8,
        minHeight: 112, padding: '15px 14px 13px', borderRadius: 8,
        border: `1px solid ${(mine || hover) ? HR.accent : HR.rule}`,
        background: mine ? HR.accentSoft : HR.surface,
        boxShadow: hover ? '0 4px 16px rgba(11,95,90,0.10)' : 'none',
        transform: hover ? 'translateY(-2px)' : 'none',
        transition: 'border-color 120ms, box-shadow 120ms, transform 120ms, background 120ms',
      }, {
        // tonal tile: s2 → s3 on hover (state layer), ordered = primary container + primary outline;
        // no lift / shadow (mockup: flat tonal surfaces)
        borderRadius: GxT.radius.card,
        border: `1px solid ${mine ? GxT.color.p : 'transparent'}`,
        background: mine ? GxT.color.pc : (hover ? GxT.color.s3 : GxT.color.s2),
        boxShadow: 'none', transform: 'none',
        transition: 'background 120ms, border-color 120ms',
      })}
    >
      {mine && (
        <span style={uiStyle({
          position: 'absolute', top: -9, right: 12,
          background: HR.accent, color: '#fff',
          fontSize: FontSizes.trading_sm, fontWeight: 600,
          padding: '2px 8px', borderRadius: 8, letterSpacing: '0.03em', whiteSpace: 'nowrap',
        }, { fontWeight: GxT.weight.label, letterSpacing: 0 })}>你點了{mineCount > 1 ? ` ×${mineCount}` : ''}</span>
      )}
      <span style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.md, color: HR.ink, lineHeight: 1.25 }, { fontWeight: GxT.weight.heading })}>
        {item.name}
      </span>
      <span style={{
        fontFamily: HR.mono, fontSize: FontSizes.sm, fontWeight: 500, color: HR.inkSoft,
        fontVariantNumeric: 'tabular-nums',
      }}>{fmtMoney(item.price)}</span>
      {/* Footer 恆單行：各段 nowrap；mine 時「再點一份」變長 → 備註縮成純 icon、
        * 隱藏 ×N 熱度徽章（與上緣「你點了 ×N」浮標重複，總份數另見餐點數量總結卡）*/}
      <span style={{
        marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 6, whiteSpace: 'nowrap',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: FontSizes.xxs, fontWeight: 600, color: HR.accent, whiteSpace: 'nowrap',
          }}>
            <Icon name="plus" size={12} stroke={2.2} />{mine ? '再點一份' : '點餐'}
          </span>
          {onOrderWithNote && (
            <span
              title="填備註後下單（如：不要辣）"
              onClick={(e) => { e.stopPropagation(); onOrderWithNote(); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                // GX tile is s2/s3: faint would drop under 4.5:1 → onV (plan §2(d)-3a)
                fontSize: FontSizes.trading_lg, color: uiPick(HR.inkFaint, GxT.color.onV), whiteSpace: 'nowrap',
                borderLeft: `1px solid ${uiPick(HR.ruleSoft, GxT.color.s4)}`, paddingLeft: 8,
                transition: 'color 120ms',
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = HR.accent}
              onMouseLeave={(e) => e.currentTarget.style.color = uiPick(HR.inkFaint, GxT.color.onV)}
            >
              <Icon name="edit" size={11} stroke={2} />{!mine && '備註'}
            </span>
          )}
        </span>
        {!mine && count > 0 && (
          <span title={`${count} 人訂了這道`} style={uiStyle({
            fontSize: FontSizes.trading_sm, padding: '1px 6px', borderRadius: 8,
            background: HR.accentSoft, color: HR.accent,
            fontFamily: HR.mono, letterSpacing: '0.02em', whiteSpace: 'nowrap', flexShrink: 0,
          }, { padding: '1px 7px', borderRadius: GxT.radius.chipSm, background: GxT.color.card, fontWeight: GxT.weight.label, letterSpacing: 0 })}>×{count}</span>
        )}
      </span>
    </button>
  );
}

/* 訂餐備註對話框 — 兩用：
 *   mode='order'：備註下單（meal 帶入餐點，送出 = 下單 + 備註）
 *   mode='edit' ：修改既有訂單備註（initial 帶入現值；清空送出 = 移除備註）
 * 上限 100 字（與後端 _NOTE_MAX_LEN 一致）。 */
const NOTE_MAX_LEN = 100;

function NoteDialog({ mode, meal, initial, onCancel, onSubmit }) {
  const [note, setNote] = React.useState(initial);
  const [busy, setBusy] = React.useState(false);
  const isOrder = mode === 'order';
  const trimmed = note.trim();
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try { await onSubmit(trimmed); } finally { setBusy(false); }
  };
  return (
    <Modal
      open onClose={busy ? undefined : onCancel}
      title={isOrder ? '備註下單' : '訂單備註'}
      subtitle={isOrder ? `${meal.name} · ${fmtMoney(meal.price ?? 0)}` : '清空內容送出即移除備註'}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={busy || (isOrder && !trimmed)}>
            {busy ? '送出中…' : isOrder ? '下單' : '儲存'}
          </Button>
        </>
      }
    >
      <textarea
        autoFocus
        value={note}
        maxLength={NOTE_MAX_LEN}
        placeholder="例：不要辣、麵換飯、去冰…"
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
        }}
        rows={3}
        style={uiStyle({
          width: '100%', resize: 'none', boxSizing: 'border-box',
          fontFamily: HR.sans, fontSize: FontSizes.sm, color: HR.ink,
          padding: '10px 12px', borderRadius: 6,
          border: `1px solid ${HR.rule}`, outline: 'none', background: HR.surface,
        }, {
          // outlined field (= hr-shell inputStyle under GX); focus ring comes from gx-css-pages
          borderRadius: GxT.radius.input, border: `1px solid ${GxT.color.ol}`,
          background: GxT.color.card,
        })}
      />
      <div style={{
        marginTop: 6, textAlign: 'right',
        fontFamily: HR.mono, fontSize: FontSizes.trading_md, color: HR.inkFaint,
      }}>{note.length}/{NOTE_MAX_LEN}</div>
    </Modal>
  );
}

/* 結算對帳卡 — 把今日訂單依部門分「交易部 / 其他」結算（份數 / 金額 / 未付）。
 * 交易部 = 員工部門 code 為 'TRD'；其餘（含未設部門、非員工帳號）一律歸「其他」。
 * 純展示，資料由 LunchPage 用 statOf() 算好後傳入。 */
function SettlementCard({ trd, other, grand }) {
  const groups = [
    { label: '交易部', s: trd },
    { label: '其他',   s: other },
  ];
  return (
    <Card className="hr-lunch-settle-card" eyebrow="Settlement" title="結算對帳"
      italic="交易部 / 其他" padding="0">
      {groups.map((g, i) => (
        <div key={g.label} style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          padding: '12px 18px', borderTop: i === 0 ? 'none' : `1px solid ${HR.ruleSoft}`,
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
            <span style={lunchGroupLabelStyle({ fontFamily: HR.serif, fontSize: FontSizes.sm, color: HR.ink })}>{g.label}</span>
            <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>{g.s.qty} 份</span>
          </span>
          <span style={{ textAlign: 'right' }}>
            <div style={{
              fontFamily: HR.mono, fontSize: FontSizes.sm, fontWeight: 600, color: HR.ink,
              fontVariantNumeric: 'tabular-nums',
            }}>{fmtMoney(g.s.total)}</div>
            <div style={{
              fontFamily: HR.mono, fontSize: FontSizes.trading_lg, marginTop: 2,
              color: g.s.unpaidQty > 0 ? HR.warn : HR.accent,
            }}>
              {g.s.qty === 0 ? '—'
                : g.s.unpaidQty > 0 ? `未付 ${g.s.unpaidQty} · ${fmtMoney(g.s.unpaidTotal)}`
                : '已結清'}
            </div>
          </span>
        </div>
      ))}
      {/* 今日合計 footer — 跟訂單表同款黑線 + tabular nums */}
      <div style={lunchTotalsRowStyle({
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        padding: '14px 18px', borderTop: `2px solid ${HR.ink}`, background: HR.surfaceAlt,
      })}>
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
          <span style={lunchGroupLabelStyle({ fontFamily: HR.serif, fontSize: FontSizes.xs, color: HR.inkSoft, letterSpacing: '0.04em' })}>今日合計</span>
          <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>{grand.qty} 份</span>
        </span>
        <span style={{ textAlign: 'right' }}>
          <div style={{
            fontFamily: HR.mono, fontSize: FontSizes.md, fontWeight: 600, color: HR.ink,
            fontVariantNumeric: 'tabular-nums',
          }}>{fmtMoney(grand.total)}</div>
          <div style={{
            fontFamily: HR.mono, fontSize: FontSizes.trading_lg, marginTop: 2,
            color: grand.unpaidQty > 0 ? HR.warn : HR.accent,
          }}>
            {grand.unpaidQty > 0 ? `未付 ${grand.unpaidQty} · ${fmtMoney(grand.unpaidTotal)}` : `全部已付 · ${grand.qty} 筆`}
          </div>
        </span>
      </div>
    </Card>
  );
}

/* ── MenuCreator ── 建立菜單（覆蓋現有）：從保存的餐廳帶入 或 OCR 新菜單
 *
 * Flow:
 *   1. 點「建立菜單」→ 開 modal → 入口選擇：「從保存的餐廳」或「OCR 新菜單」
 *   2a. 從餐廳帶入 → 直接進預覽
 *   2b. 選擇菜單照片 → OCR → 顯示辨識結果預覽
 *   3. 確認 → POST /lunch/menu/replace（自動覆蓋既有菜單）
 *      預覽可勾「同時保存為餐廳」→ 菜單建立成功後 POST /lunch/restaurants
 *      （同名 409 → 二次確認後帶 overwrite:true 重送；保存失敗不影響菜單已建立）
 *
 * 不在這裡做手動修正 — 如果 OCR 結果有誤，關掉這個 modal 後改用「編輯菜單」修。
 */
function MenuCreator({ currentCount, orderCount, onSaved }) {
  const toast = useToast();
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState('entry');   // 'entry' 入口選擇 | 'ocr' 上傳照片
  const [ocrLoading, setOcrLoading] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [dragOver, setDragOver] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  // 保存餐廳相關
  const [restaurants, setRestaurants] = React.useState(null);          // null = 載入中
  const [saveAsRestaurant, setSaveAsRestaurant] = React.useState(false);
  const [restaurantName, setRestaurantName] = React.useState('');
  const [overwritePending, setOverwritePending] = React.useState(null); // {name, items} — 409 待確認覆蓋
  const [overwriteBusy, setOverwriteBusy] = React.useState(false);
  const fileRef = React.useRef(null);

  const reset = () => {
    setItems([]); setOcrLoading(false); setDragOver(false); setConfirmOpen(false);
    setMode('entry'); setSaveAsRestaurant(false); setRestaurantName('');
  };
  const openModal = () => {
    reset(); setOpen(true);
    // 載入保存的餐廳清單 — 失敗不阻擋 OCR 流程（入口卡顯示為空）
    setRestaurants(null);
    api('GET', '/lunch/restaurants')
      .then(d => setRestaurants(d?.restaurants || []))
      .catch(() => setRestaurants([]));
  };
  const closeModal = () => { setOpen(false); reset(); };

  // 從保存的餐廳帶入 → 直接進預覽步
  const pickRestaurant = (r) => {
    const list = (r.items || []).map(x => ({ name: x.name, price: x.price }));
    if (list.length === 0) { toast('這間餐廳沒有保存任何品項', 'error'); return; }
    setItems(list);
    setSaveAsRestaurant(false);       // 預設不勾；若勾選，名稱預填該餐廳名
    setRestaurantName(r.name);
    toast(`已帶入「${r.name}」的 ${list.length} 項菜單`, 'ok');
  };

  const processFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast('只接受圖片檔', 'error'); return;
    }
    setOcrLoading(true);
    try {
      const b64 = await fileToResizedBase64(file);
      const resp = await api('POST', '/lunch/menu/ocr', { image: b64, mime: 'image/jpeg' });
      const ocrItems = flattenOcrItems(resp);
      setItems(ocrItems);
      setSaveAsRestaurant(false);   // OCR 路徑：預設不勾、名稱空白
      setRestaurantName('');
      toast(`已辨識 ${ocrItems.length} 項，請確認後儲存`, 'ok');
    } catch (err) {
      toast(err.detail || err.message || 'OCR 失敗', 'error');
    } finally {
      setOcrLoading(false);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    processFile(file);
  };

  const trySubmit = () => {
    const clean = items.filter(x => x.name?.trim());
    if (clean.length === 0) {
      toast('沒有可儲存的項目', 'error'); return;
    }
    if (saveAsRestaurant && !restaurantName.trim()) {
      toast('已勾選「同時保存為餐廳」，請輸入餐廳名稱', 'error'); return;
    }
    // 今日已有訂單 → 跳 confirm；無 → 直接送
    if (orderCount > 0) {
      setConfirmOpen(true);
    } else {
      doSubmit();
    }
  };

  /* 保存餐廳（菜單建立後的附帶動作）— 失敗只 toast，不影響已建立的菜單。
   * 同名 409 → 存入 overwritePending，由 ConfirmDialog 確認後帶 overwrite:true 重送。 */
  const saveRestaurant = async (name, itemList, overwrite = false) => {
    try {
      const resp = await api('POST', '/lunch/restaurants', {
        name, items: itemList, ...(overwrite ? { overwrite: true } : {}),
      });
      toast(resp?.overwritten ? `已覆蓋保存餐廳「${name}」` : `已保存餐廳「${name}」`, 'ok');
    } catch (e) {
      if (e.status === 409 && !overwrite) {
        setOverwritePending({ name, items: itemList });
      } else {
        toast(`保存餐廳失敗：${e.detail || '未知錯誤'}`, 'error');
      }
    }
  };

  const doSubmit = async () => {
    const clean = items
      .filter(x => x.name?.trim())
      .map(x => ({ name: x.name.trim(), price: parseInt(x.price) || 0 }));
    setSubmitting(true);
    try {
      const resp = await api('POST', '/lunch/menu/replace', { items: clean });
      const cleared = resp?.orders_cleared || 0;
      toast(
        cleared > 0
          ? `已建立新菜單（${clean.length} 項），清除 ${cleared} 筆舊訂單`
          : `已建立新菜單（${clean.length} 項）`,
        'ok',
      );
      // 菜單已建立 — 保存餐廳為附帶動作，成敗分開回報
      const rName = restaurantName.trim();
      if (saveAsRestaurant && rName) {
        await saveRestaurant(rName, clean);
      }
      closeModal();
      onSaved();
    } catch (e) {
      toast(e.detail || '建立失敗', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // 預覽階段的統計
  const previewTotal = items.reduce((s, it) => s + (parseInt(it.price) || 0), 0);
  const previewAvg = items.length > 0 ? Math.round(previewTotal / items.length) : 0;
  const unrecognized = items.filter(it => !it.name?.trim()).length;

  return (
    <>
      <Button size="sm" variant="primary" icon="upload" onClick={openModal}>建立</Button>
      {open && (
        <Modal
          open onClose={closeModal}
          title="建立菜單"
          subtitle={currentCount > 0 ? `將覆蓋現有 ${currentCount} 項菜單` : '從保存的餐廳帶入 或 OCR 辨識'}
          size="md"
          footer={
            items.length > 0 ? (
              <>
                <Button variant="ghost" onClick={closeModal} disabled={submitting}>取消</Button>
                <Button variant="primary" onClick={trySubmit} disabled={submitting}>
                  {submitting ? '儲存中…'
                    : currentCount > 0
                      ? `覆蓋並儲存（${items.length} 項）`
                      : `儲存（${items.length} 項）`}
                </Button>
              </>
            ) : (
              <Button variant="ghost" onClick={closeModal}>取消</Button>
            )
          }
        >
          <input
            ref={fileRef} type="file" accept="image/*"
            style={{ display: 'none' }} onChange={handleFile}
          />

          {/* 步驟指示 */}
          <div style={{
            display: 'flex', gap: 6, alignItems: 'center',
            fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginBottom: 14,
          }}>
            <StepDot active={mode === 'entry' && items.length === 0 && !ocrLoading} done={mode === 'ocr' || ocrLoading || items.length > 0} label="① 選擇來源" />
            <span style={{ flex: 1, height: 1, background: HR.ruleSoft }} />
            <StepDot active={(mode === 'ocr' && items.length === 0 && !ocrLoading) || ocrLoading} done={items.length > 0 && !ocrLoading} label="② 取得菜單" />
            <span style={{ flex: 1, height: 1, background: HR.ruleSoft }} />
            <StepDot active={items.length > 0 && !ocrLoading} done={false} label="③ 確認儲存" />
          </div>

          {/* 0. 入口選擇 — 從保存的餐廳帶入 或 OCR 新菜單 */}
          {mode === 'entry' && items.length === 0 && !ocrLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* 入口卡 1：從保存的餐廳 */}
              <div style={{ border: `1px solid ${HR.rule}`, borderRadius: uiPick(6, GxT.radius.inner), overflow: 'hidden' }}>
                <div style={{
                  display: 'flex', alignItems: 'baseline', gap: 8,
                  padding: '10px 14px', background: HR.surfaceAlt,
                  borderBottom: `1px solid ${HR.ruleSoft}`,
                }}>
                  <span style={{ fontSize: FontSizes.xs, fontWeight: uiPick(600, GxT.weight.heading), color: HR.ink }}>從保存的餐廳</span>
                  <span style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>點選即帶入該餐廳保存的菜單</span>
                </div>
                {restaurants === null ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '14px', color: HR.inkFaint, fontSize: FontSizes.trading_lg,
                  }}>
                    <Spinner size={14} /> 載入餐廳清單…
                  </div>
                ) : restaurants.length === 0 ? (
                  <div style={uiStyle({
                    padding: '18px 14px', fontSize: FontSizes.trading_lg,
                    color: HR.inkFaint, fontStyle: 'italic', opacity: 0.75,
                  }, { fontStyle: 'normal', opacity: 1 })}>
                    尚無保存的餐廳 — OCR 建立菜單時可勾選「同時保存為餐廳」
                  </div>
                ) : (
                  <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {restaurants.map((r, i) => (
                      <div
                        key={r.id}
                        onClick={() => pickRestaurant(r)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '11px 14px', cursor: 'pointer',
                          borderTop: i > 0 ? `1px solid ${HR.ruleSoft}` : 'none',
                          transition: 'background 120ms',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = HR.accentSoft}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <span style={uiStyle({
                          flex: 1, minWidth: 0, fontFamily: HR.serif, fontSize: FontSizes.sm, color: HR.ink,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }, { fontWeight: GxT.weight.heading })}>{r.name}</span>
                        <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_md, color: HR.inkFaint, flexShrink: 0 }}>
                          {r.item_count} 項 · 更新於 {fmtDate(r.updated_at)}
                        </span>
                        <span style={{ color: HR.accent, fontSize: FontSizes.trading_md, flexShrink: 0 }}>帶入 ›</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 入口卡 2：OCR 新菜單 */}
              <div
                onClick={() => setMode('ocr')}
                style={{
                  cursor: 'pointer', padding: '16px 14px', borderRadius: uiPick(6, GxT.radius.inner),
                  border: `1px solid ${HR.rule}`, background: HR.surface,
                  display: 'flex', alignItems: 'center', gap: 12,
                  transition: 'border-color 120ms, background 120ms',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = HR.accent;
                  e.currentTarget.style.background = HR.accentSoft;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = HR.rule;
                  e.currentTarget.style.background = HR.surface;
                }}
              >
                <span style={{ color: HR.accent }}><Icon name="upload" size={22} stroke={1.6} /></span>
                <span style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontSize: FontSizes.xs, fontWeight: uiPick(600, GxT.weight.heading), color: HR.ink }}>OCR 新菜單</span>
                  <span style={{ display: 'block', marginTop: 3, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
                    上傳菜單照片，自動辨識項目與價格
                  </span>
                </span>
                <span style={{ color: HR.accent, fontSize: FontSizes.trading_md, flexShrink: 0 }}>›</span>
              </div>
            </div>
          )}

          {/* 1. OCR — 還沒選檔 → 顯示上傳區（支援拖拉） */}
          {mode === 'ocr' && items.length === 0 && !ocrLoading && (
            <div>
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault(); setDragOver(false);
                processFile(e.dataTransfer.files?.[0]);
              }}
              style={{
                cursor: 'pointer',
                padding: '48px 20px', textAlign: 'center',
                border: `2px dashed ${dragOver ? HR.accent : HR.rule}`,
                borderRadius: uiPick(6, GxT.radius.inner),
                background: dragOver ? HR.accentSoft : HR.surfaceAlt,
                transition: 'border-color 120ms, background 120ms, transform 120ms',
                transform: dragOver ? 'scale(1.01)' : 'scale(1)',
              }}
              onMouseEnter={(e) => {
                if (dragOver) return;
                e.currentTarget.style.borderColor = HR.accent;
                e.currentTarget.style.background = HR.accentSoft;
              }}
              onMouseLeave={(e) => {
                if (dragOver) return;
                e.currentTarget.style.borderColor = HR.rule;
                e.currentTarget.style.background = HR.surfaceAlt;
              }}
            >
              <div style={{ color: HR.accent }}>
                <Icon name="upload" size={36} stroke={1.5} />
              </div>
              <div style={{ marginTop: 12, fontSize: FontSizes.sm, fontWeight: 500, color: HR.ink }}>
                {dragOver ? '放開以上傳' : '點此選擇 或 拖拉圖片進來'}
              </div>
              <div style={{ marginTop: 6, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
                支援 JPG / PNG / WebP — OCR 自動辨識項目與價格
              </div>
              {currentCount > 0 && (
                <div style={{
                  marginTop: 16, padding: '7px 14px',
                  background: HR.surfaceAlt, color: HR.inkSoft,
                  border: `1px solid ${HR.rule}`,
                  borderRadius: uiPick(3, GxT.radius.chip), fontSize: FontSizes.trading_xl, display: 'inline-flex',
                  alignItems: 'center', gap: 6,
                }}>
                  <span style={{ color: HR.warn }}>⚠</span>
                  儲存後將覆蓋目前的
                  <strong style={{ color: HR.ink, fontFamily: HR.mono }}>{currentCount}</strong>
                  項菜單
                </div>
              )}
            </div>
            {/* 返回入口選擇 */}
            <div style={{ marginTop: 10 }}>
              <Button size="sm" variant="ghost" onClick={() => setMode('entry')}>← 返回來源選擇</Button>
            </div>
            </div>
          )}

          {/* 2. OCR loading */}
          {ocrLoading && (
            <div style={{
              padding: '60px 20px', textAlign: 'center', color: HR.inkSoft,
              border: `2px dashed ${HR.rule}`, borderRadius: uiPick(6, GxT.radius.inner), background: HR.surfaceAlt,
            }}>
              <Spinner size={22} />
              <div style={{ marginTop: 14, fontSize: FontSizes.xs, color: HR.ink, fontWeight: 500 }}>
                辨識中… 請稍候
              </div>
              <div style={{ marginTop: 4, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
                圖片越大需要的時間越長
              </div>
            </div>
          )}

          {/* 3. OCR 完成 → 預覽 */}
          {items.length > 0 && !ocrLoading && (
            <div>
              <div style={{
                marginBottom: 12, padding: '10px 14px',
                background: HR.accentSoft, color: HR.accent,
                borderRadius: uiPick(4, GxT.radius.inner), fontSize: FontSizes.xxs, lineHeight: 1.5,
                display: 'flex', alignItems: 'flex-start', gap: 8,
              }}>
                <span style={{ fontSize: FontSizes.sm, lineHeight: 1 }}>✓</span>
                <div>
                  辨識完成 <strong style={{ fontFamily: HR.mono }}>{items.length}</strong> 項
                  {unrecognized > 0 && (
                    <span style={{ color: HR.warn, marginLeft: 6 }}>
                      （其中 {unrecognized} 項未辨識）
                    </span>
                  )}
                  ，若有誤可儲存後改用「編輯菜單」修正。
                </div>
              </div>

              <div style={{
                border: `1px solid ${HR.rule}`, borderRadius: uiPick(4, GxT.radius.inner),
                maxHeight: 320, overflowY: 'auto',
                fontSize: FontSizes.xs,
              }}>
                {items.map((it, i) => (
                  <div key={i}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 14px',
                      background: i % 2 === 0 ? 'transparent' : HR.surfaceAlt,
                      borderBottom: i < items.length - 1 ? `1px solid ${HR.ruleSoft}` : 'none',
                      transition: 'background 120ms',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = HR.accentSoft}
                    onMouseLeave={(e) => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : HR.surfaceAlt}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                      <span style={{
                        fontFamily: HR.mono, fontSize: FontSizes.trading_md,
                        color: HR.inkFaint, width: 20,
                      }}>{String(i + 1).padStart(2, '0')}</span>
                      {it.name || <em style={{ color: HR.inkFaint }}>（未辨識）</em>}
                    </span>
                    <span style={{
                      fontFamily: HR.mono, fontWeight: 500,
                      fontVariantNumeric: 'tabular-nums',
                      color: it.price > 0 ? HR.ink : HR.inkFaint,
                    }}>
                      {fmtMoney(it.price)}
                    </span>
                  </div>
                ))}
              </div>

              {/* 預覽底部：總計 + 重選 */}
              <div style={{
                marginTop: 12, padding: '10px 14px',
                background: HR.surfaceAlt, borderRadius: uiPick(4, GxT.radius.inner),
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                fontSize: FontSizes.xxs,
              }}>
                <span style={{ color: HR.inkFaint }}>
                  共 <strong style={{ fontFamily: HR.mono, color: HR.ink }}>{items.length}</strong> 項
                  ・ 平均單價 <strong style={{ fontFamily: HR.mono, color: HR.ink }}>{fmtMoney(previewAvg)}</strong>
                </span>
                <Button size="sm" variant="ghost" icon="upload" onClick={() => fileRef.current?.click()}>
                  重新選擇照片
                </Button>
              </div>

              {/* 同時保存為餐廳 — 菜單建立成功後 POST /lunch/restaurants（失敗不影響菜單） */}
              <div style={{
                marginTop: 10, padding: '10px 14px',
                border: `1px dashed ${HR.rule}`, borderRadius: uiPick(4, GxT.radius.inner),
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              }}>
                <Checkbox
                  checked={saveAsRestaurant}
                  onChange={setSaveAsRestaurant}
                  label="同時保存為餐廳"
                />
                {saveAsRestaurant ? (
                  <span style={{ flex: 1, minWidth: 180 }}>
                    <TextInput
                      value={restaurantName}
                      onChange={setRestaurantName}
                      placeholder="餐廳名稱（必填）"
                    />
                  </span>
                ) : (
                  <span style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontStyle: uiPick('italic', 'normal') }}>
                    下次建菜單可直接從「從保存的餐廳」帶入
                  </span>
                )}
              </div>
            </div>
          )}
        </Modal>
      )}
      {/* 今日已有訂單 → 額外二次確認 */}
      <ConfirmDialog
        open={confirmOpen}
        title="覆蓋菜單與今日訂單"
        message={
          `今日已有 ${orderCount} 筆訂單。建立新菜單會清除這些訂單（含未付 / 已付），且無法復原。\n\n` +
          `確認用辨識後的 ${items.length} 項新菜單覆蓋？`
        }
        danger
        confirmLabel={`覆蓋並清除 ${orderCount} 筆訂單`}
        busy={submitting}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={async () => { setConfirmOpen(false); await doSubmit(); }}
      />
      {/* 保存餐廳撞名（409）→ 確認覆蓋。獨立於主 modal（菜單此時已建立成功） */}
      <ConfirmDialog
        open={!!overwritePending}
        title="覆蓋保存的餐廳"
        message={overwritePending
          ? `餐廳「${overwritePending.name}」已存在，要覆蓋保存的菜單嗎？\n\n（今日菜單已建立成功，此步驟只影響保存的餐廳）`
          : ''}
        confirmLabel="覆蓋保存"
        busy={overwriteBusy}
        onCancel={() => setOverwritePending(null)}
        onConfirm={async () => {
          const pending = overwritePending;
          setOverwriteBusy(true);
          try {
            await saveRestaurant(pending.name, pending.items, true);
          } finally {
            setOverwriteBusy(false);
            setOverwritePending(null);
          }
        }}
      />
    </>
  );
}

/* Step indicator dot for MenuCreator wizard */
function StepDot({ active, done, label }) {
  const color = done ? HR.accent : active ? HR.ink : HR.inkFaint;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      color, fontWeight: active || done ? 600 : 400,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

/* ── MenuEditor ── 手動編輯既有菜單（給修 OCR 錯誤 / 加減項目用）
 *
 * 不含 OCR — OCR 進入點在「建立菜單」(MenuCreator)。這裡只負責對既有 items 做手動 CRUD。
 */
function MenuEditor({ initial, onSaved }) {
  const toast = useToast();
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState([]);
  // 只在開啟時複製一次 initial — 不跟 props 連動，否則 30s polling 換新 menu 陣列會洗掉未存的編輯
  const openEditor = () => { setItems((initial || []).map(x => ({ ...x }))); setOpen(true); };

  const upd    = (i, k, v) => setItems(arr => arr.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const add    = ()        => setItems(arr => [...arr, { name: '', price: 0 }]);
  const remove = (i)       => setItems(arr => arr.filter((_, idx) => idx !== i));

  const submit = async () => {
    const clean = items.filter(x => x.name?.trim());
    try {
      const resp = await api('PUT', '/lunch/menu', {
        items: clean.map(x => ({ name: x.name.trim(), price: parseInt(x.price) || 0 })),
      });
      const synced = resp?.orders_synced || 0;
      toast(
        synced > 0
          ? `菜單已更新（連動調整 ${synced} 筆未付款訂單價格）`
          : '菜單已更新',
        'ok',
      );
      setOpen(false);
      onSaved();
    } catch (e) { toast(e.detail || '失敗', 'error'); }
  };

  // 統計（過濾掉空項目）
  const validItems = items.filter(x => x.name?.trim());
  const totalAmt = validItems.reduce((s, it) => s + (parseInt(it.price) || 0), 0);
  const emptyRows = items.length - validItems.length;

  return (
    <>
      <Button size="sm" variant="ghost" icon="edit" onClick={openEditor}>編輯</Button>
      {open && (
        <Modal
          open onClose={() => setOpen(false)}
          title="編輯菜單"
          subtitle="手動修正項目與價格"
          size="md"
          footer={<>
            <Button variant="ghost" onClick={() => setOpen(false)}>取消</Button>
            <Button variant="primary" onClick={submit} disabled={validItems.length === 0}>儲存</Button>
          </>}
        >
          {/* 空狀態 */}
          {items.length === 0 && (
            <div style={{
              padding: '40px 20px', textAlign: 'center',
              border: `1px dashed ${HR.rule}`, borderRadius: uiPick(6, GxT.radius.inner),
              background: HR.surfaceAlt,
            }}>
              <div style={{ color: HR.inkFaint }}>
                <Icon name="coffee" size={28} stroke={1.5} />
              </div>
              <div style={{ marginTop: 10, fontSize: FontSizes.xs, color: HR.ink, fontWeight: 500 }}>
                目前沒有項目
              </div>
              <div style={{ marginTop: 6, fontSize: FontSizes.trading_lg, color: HR.inkFaint, lineHeight: 1.6 }}>
                可手動新增，或關閉後改用「建立菜單」用照片 OCR 快速建立
              </div>
            </div>
          )}

          {/* 項目列表 */}
          {items.length > 0 && (
            <div style={{
              border: `1px solid ${HR.rule}`, borderRadius: uiPick(4, GxT.radius.inner),
              overflow: 'hidden', marginBottom: 12,
            }}>
              {/* Header */}
              <div style={uiStyle({
                display: 'grid', gridTemplateColumns: '28px 1fr 110px 32px', gap: 8,
                padding: '8px 12px', background: HR.surfaceAlt,
                fontSize: FontSizes.trading_md, color: HR.inkFaint,
                letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 500,
              }, { color: GxT.color.onV, letterSpacing: 0, textTransform: 'none', fontWeight: GxT.weight.label })}>
                <span>#</span>
                <span>餐點名稱</span>
                <span style={{ textAlign: 'right' }}>價格</span>
                <span />
              </div>
              {items.map((it, i) => {
                const isEmpty = !it.name?.trim();
                return (
                  <div key={i}
                    className="menu-edit-row"
                    style={{
                      display: 'grid', gridTemplateColumns: '28px 1fr 110px 32px',
                      gap: 8, alignItems: 'center',
                      padding: '8px 12px',
                      borderTop: i > 0 ? `1px solid ${HR.ruleSoft}` : 'none',
                      transition: 'background 120ms',
                      background: isEmpty ? uiPick('#FFF8F0', GxCore.tone('warn').bg) : 'transparent',
                    }}
                    onMouseEnter={(e) => { if (!isEmpty) e.currentTarget.style.background = HR.surfaceAlt; }}
                    onMouseLeave={(e) => { if (!isEmpty) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{
                      fontFamily: HR.mono, fontSize: FontSizes.trading_md, color: HR.inkFaint,
                      textAlign: 'center',
                    }}>{String(i + 1).padStart(2, '0')}</span>
                    <TextInput value={it.name} onChange={(v) => upd(i, 'name', v)} placeholder="餐點名稱" />
                    <input
                      type="number" value={it.price}
                      onChange={(e) => upd(i, 'price', e.target.value)}
                      style={inputStyle({
                        fontFamily: HR.mono, textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                      })}
                    />
                    <button
                      onClick={() => remove(i)}
                      title="刪除這項"
                      style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: HR.inkFaint, fontSize: FontSizes.lg, lineHeight: 1,
                        padding: 4, borderRadius: uiPick(3, GxT.radius.chip),
                        transition: 'color 120ms, background 120ms',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = HR.down; e.currentTarget.style.background = uiPick('#FCE4E4', GxCore.tone('down').bg); }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = HR.inkFaint; e.currentTarget.style.background = 'transparent'; }}
                    >×</button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer: 新增按鈕 + 統計 */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, marginTop: items.length === 0 ? 14 : 0,
          }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="sm" variant="ghost" icon="plus" onClick={add}>新增項目</Button>
              {items.length > 0 && (
                <Button size="sm" variant="ghost" icon="x" onClick={() => setItems([])}>全部清除</Button>
              )}
            </div>
            {validItems.length > 0 && (
              <span style={{ fontSize: FontSizes.trading_xl, color: HR.inkFaint }}>
                共 <strong style={{ fontFamily: HR.mono, color: HR.ink }}>{validItems.length}</strong> 項
                {emptyRows > 0 && (
                  <span style={{ color: HR.warn, marginLeft: 6 }}>
                    （{emptyRows} 項待填）
                  </span>
                )}
                ・ 總額 <strong style={{ fontFamily: HR.mono, color: HR.ink, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtMoney(totalAmt)}
                </strong>
              </span>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

/* ── RestaurantManager ── 管理保存的餐廳（列表 / 用今日菜單更新 / 刪除）
 *
 * 保存的餐廳與今日菜單彼此獨立（items 是複製非引用）：
 * 從餐廳帶入後當日修改「不會」自動回寫保存版，需在這裡按「用今日菜單更新」手動覆蓋。
 * 帶入請走「建立」(MenuCreator)；這裡只負責保存版的維護。
 */
function RestaurantManager({ currentMenu }) {
  const toast = useToast();
  const [open, setOpen] = React.useState(false);
  const [restaurants, setRestaurants] = React.useState(null);       // null = 載入中
  const [confirmAction, setConfirmAction] = React.useState(null);   // { type: 'update' | 'delete', r }
  const [busy, setBusy] = React.useState(false);

  const load = () => api('GET', '/lunch/restaurants')
    .then(d => setRestaurants(d?.restaurants || []))
    .catch(e => { toast(e.detail || '載入餐廳清單失敗', 'error'); setRestaurants([]); });

  const openModal = () => { setRestaurants(null); setOpen(true); load(); };

  // 今日菜單（過濾空項），「用今日菜單更新」的覆蓋內容
  const todayItems = (currentMenu || [])
    .filter(x => x.name?.trim())
    .map(x => ({ name: x.name.trim(), price: parseInt(x.price) || 0 }));

  const doConfirm = async () => {
    if (!confirmAction) return;
    const { type, r } = confirmAction;
    setBusy(true);
    try {
      if (type === 'update') {
        await api('PUT', `/lunch/restaurants/${r.id}`, { items: todayItems });
        toast(`已用今日菜單更新「${r.name}」（${todayItems.length} 項）`, 'ok');
      } else {
        await api('DELETE', `/lunch/restaurants/${r.id}`);
        toast(`已刪除餐廳「${r.name}」`, 'ok');
      }
      setConfirmAction(null);
      await load();
    } catch (e) {
      toast(e.detail || (type === 'update' ? '更新失敗' : '刪除失敗'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="ghost" icon="coffee" onClick={openModal}>餐廳</Button>
      {open && (
        <Modal
          open onClose={() => setOpen(false)}
          title="保存的餐廳"
          subtitle="帶入菜單請走「建立」；這裡維護保存版"
          size="md"
          footer={<Button variant="ghost" onClick={() => setOpen(false)}>關閉</Button>}
        >
          {restaurants === null ? (
            <div style={{ padding: '36px 0', textAlign: 'center', color: HR.inkFaint }}>
              <Spinner size={18} />
              <div style={{ marginTop: 10, fontSize: FontSizes.trading_lg }}>載入餐廳清單…</div>
            </div>
          ) : restaurants.length === 0 ? (
            <EmptyState
              icon="coffee"
              title="尚無保存的餐廳"
              hint="建立菜單時勾選「同時保存為餐廳」即可保存"
            />
          ) : (
            <div style={{ border: `1px solid ${HR.rule}`, borderRadius: uiPick(4, GxT.radius.inner), overflow: 'hidden' }}>
              {restaurants.map((r, i) => (
                <div
                  key={r.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '11px 14px',
                    borderTop: i > 0 ? `1px solid ${HR.ruleSoft}` : 'none',
                    transition: 'background 120ms',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = HR.surfaceAlt}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={uiStyle({
                      fontFamily: HR.serif, fontSize: FontSizes.sm, color: HR.ink,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }, { fontWeight: GxT.weight.heading })}>{r.name}</div>
                    <div style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_md, color: HR.inkFaint, marginTop: 2 }}>
                      {r.item_count} 項 · 更新於 {fmtDate(r.updated_at)}
                    </div>
                  </div>
                  <Button
                    size="sm" variant="ghost"
                    disabled={todayItems.length === 0}
                    title={todayItems.length === 0 ? '今日尚無菜單，無法更新' : `用今日 ${todayItems.length} 項菜單覆蓋保存版`}
                    onClick={() => setConfirmAction({ type: 'update', r })}
                  >用今日菜單更新</Button>
                  <Button
                    size="sm" variant="ghost"
                    style={{ color: HR.down }}
                    onClick={() => setConfirmAction({ type: 'delete', r })}
                  >刪除</Button>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
      <ConfirmDialog
        open={!!confirmAction}
        title={confirmAction?.type === 'delete' ? '刪除保存的餐廳' : '用今日菜單更新'}
        message={!confirmAction ? '' : confirmAction.type === 'delete'
          ? `將永久刪除保存的餐廳「${confirmAction.r.name}」（${confirmAction.r.item_count} 項菜單）。\n` +
            `不影響任何既有菜單與訂單。此操作無法復原。\n\n確認刪除？`
          : `用今日 ${todayItems.length} 項菜單覆蓋「${confirmAction.r.name}」保存的 ${confirmAction.r.item_count} 項？\n\n` +
            `覆蓋後無法復原。`}
        danger={confirmAction?.type === 'delete'}
        confirmLabel={confirmAction?.type === 'delete' ? '確認刪除' : '確認覆蓋'}
        busy={busy}
        onCancel={() => setConfirmAction(null)}
        onConfirm={doConfirm}
      />
    </>
  );
}

window.LunchPage = LunchPage;
